import React, { useRef, useState, useEffect, useCallback } from "react";
import BottomNav from "../common/BottomNav";
import StatusModal from "../modal/StatusModal";
import AppHeader from "../common/AppHeader";
import ButtonSpinner from "../common/ButtonSpinner";
import Toast from "../common/Toast";
import Select from "react-select";
import { useNavigate, Link } from "react-router-dom";
import DocumentUploadModal from "../modal/DocumentUploadModal";
import ServiceImageModal from "../modal/ServiceImageModal";
import { addEventServiceApi } from "../../services/eventService";
import { uploadImageApi, getMembershipStatus } from "../../services/coreService";
import { getOptimizedBase64ForUpload } from "../../utils/imageUploadOptimizer";
import { useTranslation } from "react-i18next";
import {
  ID_PROOF_TYPE_OPTIONS,
  generateServiceDescription,
  getDescriptionSuggestions,
} from "../utils/serviceCategories";
import { generateAIDescriptionAsync } from "../../services/aiDescriptionService";
import {
  getAllCategories,
  formatCategoriesForDropdown,
  isIconUrl,
} from "../../services/categoryService";
import styles from "../css/AddListingService.module.css";

// Helper function to render category option with icon
const formatCategoryOption = (option) => {
  if (!option) return null;
  
  const icon = option.icon;
  const isUrl = isIconUrl(icon);
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {isUrl ? (
        <img 
          src={icon} 
          alt="" 
          style={{ 
            width: '20px', 
            height: '20px', 
            objectFit: 'contain',
            flexShrink: 0
          }} 
        />
      ) : (
        <span style={{ fontSize: '18px', flexShrink: 0 }}>{icon}</span>
      )}
      <span>{option.label}</span>
    </div>
  );
};

const selectStyles = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base) => ({
    ...base,
    minHeight: 50,
    borderRadius: 10,
    border: "2px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: 15,
    fontWeight: 500,
    boxShadow: "none",
    paddingLeft: 4,
    paddingRight: 4,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
  }),
  group: (base) => ({
    ...base,
    paddingTop: 8,
    paddingBottom: 8,
  }),
  groupHeading: (base) => ({
    ...base,
    fontSize: 13,
    fontWeight: 600,
    color: "#667eea",
    textTransform: "uppercase",
    paddingLeft: 12,
    paddingBottom: 4,
  }),
};

export default function AddEventService() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user.spId || "";
  const addressInputRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [form, setForm] = useState({
    esId: "",
    ownerName: user.name || "",
    primaryContact: user.phone || "",
    whatsappNumber: "",
    businessName: "",
    address: "",
    pincode: "",
    city: "",
    state: "",
    latitude: "",
    longitude: "",
    serviceDescription: "",
    priceRange: "",
    minPrice: "",
    maxPrice: "",
    status: "inactive",
    approvalStatus: "pending",
    serviceImageUrls: "",
    ownerIdProofType: "",
    ownerIdProofNumber: "",
    ownerIdProofUrl: "",
    gstNumber: "",
    businessRegistrationNumber: "",
    businessLicenseUrl: "",
    fssaiLicenseNumber: "",
    fssaiLicenseUrl: "",
    insuranceCertificateUrl: "",
    experience: "",
    availableOnWeekends: true,
    advanceBookingDays: "7",
  });

  const [modal, setModal] = useState({
    open: false,
    message: "",
    success: true,
  });
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [showIdProofModal, setShowIdProofModal] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showBusinessLicenseModal, setShowBusinessLicenseModal] =
    useState(false);
  const [showFssaiModal, setShowFssaiModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [mainCategory, setMainCategory] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiLanguage, setAiLanguage] = useState("en");
  const [idProofType, setIdProofType] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [tempDescription, setTempDescription] = useState("");
  const descriptionTextareaRef = useRef(null);
  // State for API-loaded categories
  const [mainCategoryOptions, setMainCategoryOptions] = useState([]);
  const [groupedServiceCategories, setGroupedServiceCategories] = useState({});
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Function to load categories from API
  const loadCategories = useCallback(async () => {
      try {
        // Check if user is logged in
        if (!userId) {
          setToastMessage('Please login to continue');
          setShowToast(true);
          setCategoriesLoading(false);
          return;
        }
        
        setCategoriesLoading(true);
        const categories = await getAllCategories();
        
        if (!categories || categories.length === 0) {
          setToastMessage('No categories available. Please contact support.');
          setShowToast(true);
          setCategoriesLoading(false);
          return;
        }
        
        // Format for dropdown options
        const mainOptions = formatCategoriesForDropdown(categories);
        setMainCategoryOptions(mainOptions);
        
        // Format with subcategories - keep icons from categories
        const transformedGrouped = {};
        categories.forEach(category => {
          if (category.subCategories && Array.isArray(category.subCategories)) {
            // Use just the name as key since we're rendering icon separately
            const categoryKey = category.name;
            transformedGrouped[categoryKey] = category.subCategories.map(sub => ({
              value: sub.name,
              label: sub.name,
              icon: category.icon, // Use parent category icon for subcategories
            }));
          }
        });
        
        setGroupedServiceCategories(transformedGrouped);
      } catch (error) {
        console.error('Error loading categories:', error);
        setToastMessage('Failed to load categories: ' + (error.message || 'Unknown error'));
        setShowToast(true);
      } finally {
        setCategoriesLoading(false);
      }
  }, [userId]);

  // Load categories from API on component mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Check membership status on component mount
  useEffect(() => {
    const checkMembershipStatus = async () => {
      try {
        if (!userId) {
          return;
        }

        const membershipStatus = await getMembershipStatus(userId);
        console.log('Membership status check:', membershipStatus);
        
        if (!(membershipStatus && membershipStatus.responseCode === 200 && membershipStatus.responseData)) {
          // No active membership, show toast and redirect
          setToastMessage(t('Membership.membership_required_message', 'Please activate your membership to add or manage listings.'));
          setShowToast(true);
          setTimeout(() => {
            navigate('/membership');
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking membership:', error);
        setToastMessage(t('Membership.membership_required_message', 'Please activate your membership to add or manage listings.'));
        setShowToast(true);
        setTimeout(() => {
          navigate('/membership');
        }, 2000);
      }
    };

    checkMembershipStatus();
  }, [userId, navigate, t]);

  useEffect(() => {
    let autocomplete;
    let listener;
    let retryInterval = null;

    const initAutocomplete = () => {
      if (!addressInputRef.current) return false;
      if (!(window.google && window.google.maps && window.google.maps.places))
        return false;

      autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          types: ["geocode"],
          componentRestrictions: { country: ["in", "np"] },
        },
      );

      listener = autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        
        // Validate that a place with geometry was selected
        if (!place.geometry) {
          console.warn('No geometry found for selected place');
          return;
        }
        
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();

        let city = "";
        let state = "";
        let pincode = "";
        
        if (
          place.address_components &&
          Array.isArray(place.address_components)
        ) {
          for (const comp of place.address_components) {
            // City - check multiple possible types
            if (!city) {
              if (comp.types.includes("locality")) {
                city = comp.long_name;
              } else if (comp.types.includes("administrative_area_level_2")) {
                city = comp.long_name;
              } else if (comp.types.includes("administrative_area_level_3")) {
                city = comp.long_name;
              } else if (comp.types.includes("sublocality_level_1")) {
                city = comp.long_name;
              }
            }
            
            // State
            if (!state && comp.types.includes("administrative_area_level_1")) {
              state = comp.long_name;
            }
            
            // Pincode
            if (!pincode && comp.types.includes("postal_code")) {
              pincode = comp.long_name;
            }
          }
        }

        console.log('📍 Address selected:', {
          address: place.formatted_address,
          city,
          state,
          pincode,
          lat,
          lng
        });

        setForm((prev) => ({
          ...prev,
          address: place.formatted_address || place.name || "",
          latitude: lat ?? "",
          longitude: lng ?? "",
          city,
          state,
          pincode,
        }));
      });

      return true;
    };

    if (!initAutocomplete()) {
      retryInterval = setInterval(() => {
        if (initAutocomplete() && retryInterval) {
          clearInterval(retryInterval);
          retryInterval = null;
        }
      }, 300);
    }

    return () => {
      if (retryInterval) clearInterval(retryInterval);
      if (
        listener &&
        window.google &&
        window.google.maps &&
        window.google.maps.event
      ) {
        try {
          window.google.maps.event.removeListener(listener);
        } catch (err) {}
      }
      autocomplete = null;
      listener = null;
    };
  }, []);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setModal({
        open: true,
        message: t("AddEventService.geolocation_not_supported", "Geolocation is not supported by your browser"),
        success: false
      });
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          if (window.google && window.google.maps && window.google.maps.Geocoder) {
            const geocoder = new window.google.maps.Geocoder();
            const result = await new Promise((resolve, reject) => {
              geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                if (status === 'OK' && results[0]) {
                  resolve(results[0]);
                } else {
                  reject(new Error('Reverse geocoding failed'));
                }
              });
            });
            
            let city = "";
            let state = "";
            let pincode = "";
            
            if (result.address_components && Array.isArray(result.address_components)) {
              for (const comp of result.address_components) {
                // City - check multiple possible types
                if (!city) {
                  if (comp.types.includes("locality")) {
                    city = comp.long_name;
                  } else if (comp.types.includes("administrative_area_level_2")) {
                    city = comp.long_name;
                  } else if (comp.types.includes("administrative_area_level_3")) {
                    city = comp.long_name;
                  } else if (comp.types.includes("sublocality_level_1")) {
                    city = comp.long_name;
                  }
                }
                
                // State
                if (!state && comp.types.includes("administrative_area_level_1")) {
                  state = comp.long_name;
                }
                
                // Pincode
                if (!pincode && comp.types.includes("postal_code")) {
                  pincode = comp.long_name;
                }
              }
            }
            
            console.log('📍 Current location:', {
              address: result.formatted_address,
              city,
              state,
              pincode,
              latitude,
              longitude
            });
            
            setForm(prev => ({
              ...prev,
              address: result.formatted_address || "",
              latitude: latitude,
              longitude: longitude,
              city,
              state,
              pincode
            }));
            
          } else {
            // If Google Maps is not available, just set coordinates
            setForm(prev => ({
              ...prev,
              latitude: latitude,
              longitude: longitude
            }));
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          setForm(prev => ({
            ...prev,
            latitude: latitude,
            longitude: longitude
          }));
        }
        
        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        let errorMessage = t("AddEventService.location_error", "Unable to retrieve your location");
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t("AddEventService.location_permission_denied", "Location permission denied. Please enable location access in your browser settings.");
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t("AddEventService.location_unavailable", "Location information is unavailable.");
            break;
          case error.TIMEOUT:
            errorMessage = t("AddEventService.location_timeout", "Location request timed out.");
            break;
          default:
            errorMessage = t("AddEventService.location_error", "Unable to retrieve your location");
        }
        
        setModal({
          open: true,
          message: errorMessage,
          success: false
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const openDescriptionModal = () => {
    setTempDescription(form.serviceDescription);
    setShowDescriptionModal(true);
  };

  const closeDescriptionModal = () => {
    setShowDescriptionModal(false);
    setTempDescription("");
  };

  const handleGenerateAIDescription = async (target = "form") => {
    if (!mainCategory || serviceCategories.length === 0) {
      setToastMessage("Please select category and sub-category first");
      setShowToast(true);
      return;
    }

    setAiGenerating(true);
    try {
      const normalizedCategories = serviceCategories.map((service) => ({
        ...service,
        label: String(service.label || service.value || "").replace(/^[^a-zA-Z0-9]+\s*/, "").trim(),
      }));

      const aiDescription = await generateAIDescriptionAsync({
        serviceCategories: normalizedCategories,
        businessName: form.businessName,
        experience: form.experience,
        city: form.city,
        state: form.state,
        ownerName: form.ownerName,
        language: aiLanguage,
      });

      if (target === "temp") {
        setTempDescription(aiDescription);
      } else {
        setForm({ ...form, serviceDescription: aiDescription });
      }
    } catch (error) {
      console.error("AI generation failed:", error);
      const basicDesc = generateServiceDescription(
        serviceCategories,
        form.experience,
        form.teamSize,
      );

      if (target === "temp") {
        setTempDescription(basicDesc);
      } else {
        setForm({ ...form, serviceDescription: basicDesc });
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const saveDescription = () => {
    setForm({ ...form, serviceDescription: tempDescription });
    closeDescriptionModal();
  };

  const insertFormatting = (format) => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = tempDescription.substring(start, end);
    let newText = tempDescription;

    switch (format) {
      case 'bold':
        newText = tempDescription.substring(0, start) + '**' + selectedText + '**' + tempDescription.substring(end);
        break;
      case 'italic':
        newText = tempDescription.substring(0, start) + '*' + selectedText + '*' + tempDescription.substring(end);
        break;
      case 'bullet':
        newText = tempDescription.substring(0, start) + '\n• ' + (selectedText || 'List item') + tempDescription.substring(end);
        break;
      case 'number':
        newText = tempDescription.substring(0, start) + '\n1. ' + (selectedText || 'List item') + tempDescription.substring(end);
        break;
      default:
        break;
    }

    setTempDescription(newText);
    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.ownerName?.trim())
      newErrors.ownerName = t("AddEventService.validation.owner_name_required");
    if (!form.primaryContact?.trim()) {
      newErrors.primaryContact = t(
        "AddEventService.validation.primary_contact_required",
      );
    } else if (!/^[0-9]{10}$/.test(form.primaryContact.trim())) {
      newErrors.primaryContact = t("AddEventService.validation.valid_mobile");
    }
    if (
      form.whatsappNumber?.trim() &&
      !/^[0-9]{10}$/.test(form.whatsappNumber.trim())
    ) {
      newErrors.whatsappNumber = t("AddEventService.validation.valid_mobile");
    }
    if (!form.businessName?.trim())
      newErrors.businessName = t(
        "AddEventService.validation.business_name_required",
      );
    if (!mainCategory)
      newErrors.mainCategory = t(
        "AddEventService.validation.main_category_required",
      );
    if (!serviceCategories || serviceCategories.length === 0)
      newErrors.serviceCategory = t(
        "AddEventService.validation.service_category_required",
      );
    if (!form.address?.trim())
      newErrors.address = t("AddEventService.validation.address_required");
    if (!form.pincode?.trim())
      newErrors.pincode = t("AddEventService.validation.pincode_required");
    if (!form.city?.trim())
      newErrors.city = t("AddEventService.validation.city_required");
    if (!form.state?.trim())
      newErrors.state = t("AddEventService.validation.state_required");
    if (!form.serviceImageUrls?.trim())
      newErrors.serviceImageUrls = t(
        "AddEventService.validation.service_image_required",
      );
    if (!form.minPrice || Number(form.minPrice) <= 0)
      newErrors.minPrice = t("AddEventService.validation.valid_min_price");

    // GST validation (optional but recommended)
    if (
      form.gstNumber &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        form.gstNumber.trim(),
      )
    ) {
      newErrors.gstNumber = t("AddEventService.validation.valid_gst");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setModal({
        open: true,
        message: "Please fill all required fields correctly",
        success: false,
      });
      return;
    }

    setLoading(true);

    // Geocode address if coordinates are missing or 0
    let finalLatitude = form.latitude;
    let finalLongitude = form.longitude;
    
    if ((!finalLatitude || finalLatitude === 0) && form.address) {
      try {
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: form.address }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0]);
              } else {
                reject(new Error('Geocoding failed'));
              }
            });
          });
          
          finalLatitude = result.geometry.location.lat();
          finalLongitude = result.geometry.location.lng();
          console.log('Geocoded coordinates:', { lat: finalLatitude, lng: finalLongitude });
        }
      } catch (error) {
        console.warn('Failed to geocode address:', error);
        // Continue with 0,0 if geocoding fails
      }
    }

    // Build payload without esId (auto-generated by backend)
    const { esId, priceRange, ...formData } = form;

    const payload = {
      ...formData,
      spId: userId,
      category: mainCategory ? mainCategory.value : "",
      subCategory: serviceCategories.map((cat) => cat.value).join(","),
      ownerIdProofType: idProofType ? idProofType.value : "",
      latitude: finalLatitude && finalLatitude !== 0 ? Number(finalLatitude) : 0,
      longitude: finalLongitude && finalLongitude !== 0 ? Number(finalLongitude) : 0,
      minPrice: Number(form.minPrice),
      maxPrice: form.maxPrice ? Number(form.maxPrice) : Number(form.minPrice),
      experience: form.experience ? Number(form.experience) : 0,
      advanceBookingDays: Number(form.advanceBookingDays),
      availableOnWeekends: !!form.availableOnWeekends,
    };

    try {
      const res = await addEventServiceApi(payload);
      if (res && res.success) {
        setModal({
          open: true,
          message: t("AddEventService.service_added_success"),
          success: true,
        });
        resetForm();
      } else {
        setModal({
          open: true,
          message:
            res.responseMessage || t("AddEventService.service_add_failed"),
          success: false,
        });
      }
    } catch {
      setModal({
        open: true,
        message: t("AddEventService.service_add_failed"),
        success: false,
      });
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      esId: "",
      ownerName: user.name || "",
      primaryContact: user.phone || "",
      whatsappNumber: "",
      businessName: "",
      address: "",
      pincode: "",
      city: "",
      state: "",
      latitude: "",
      longitude: "",
      serviceDescription: "",
      priceRange: "",
      minPrice: "",
      maxPrice: "",
      status: "inactive",
      approvalStatus: "pending",
      serviceImageUrls: "",
      ownerIdProofType: "",
      ownerIdProofNumber: "",
      ownerIdProofUrl: "",
      gstNumber: "",
      businessRegistrationNumber: "",
      businessLicenseUrl: "",
      fssaiLicenseNumber: "",
      fssaiLicenseUrl: "",
      insuranceCertificateUrl: "",
      experience: "",
      availableOnWeekends: true,
      advanceBookingDays: "7",
    });
    setMainCategory(null);
    setServiceCategories([]);
    setIdProofType(null);
    setErrors({});
  };

  return (
    <div className={styles.pageContainer}>
      <AppHeader
        title={t("AddEventService.add_listing", "Add Listing")}
        showBack={true}
      />

      <div className={styles.contentWrapper}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.headerDecoration1} />
            <div className={styles.headerDecoration2} />
            <div className={styles.headerContent}>
              <div className={styles.headerTitle}>
                {t("AddEventService.service_registration", "Service Registration")}
              </div>
              <div className={styles.headerSubtitle}>
                {t("AddEventService.fill_details_description", "Fill in the details below to list your service")}
              </div>
            </div>
          </div>

          <form
            className={styles.formContainer}
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className={styles.sectionTitle}>
              {t("AddEventService.owner_business_details")}
            </div>

            <InputField
              label={t("AddEventService.owner_name")}
              name="ownerName"
              value={form.ownerName}
              onChange={handleChange}
              error={errors.ownerName}
              required
            />
            <InputField
              label={t("AddEventService.primary_contact")}
              name="primaryContact"
              value={form.primaryContact}
              onChange={handleChange}
              error={errors.primaryContact}
              type="tel"
              maxLength="10"
              required
            />
            <InputField
              label={t("AddEventService.whatsapp_number")}
              name="whatsappNumber"
              value={form.whatsappNumber}
              onChange={handleChange}
              error={errors.whatsappNumber}
              type="tel"
              maxLength="10"
              placeholder={t("AddEventService.optional")}
            />
            <InputField
              label={t("AddEventService.business_service_name")}
              name="businessName"
              value={form.businessName}
              onChange={handleChange}
              error={errors.businessName}
              required
            />

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.main_category")}
                <span className={styles.required}>*</span>
              </label>
              <Select
                options={mainCategoryOptions}
                value={mainCategory}
                onChange={(selected) => {
                  setMainCategory(selected);
                  // Filter out sub-categories that are not in selected main category
                  if (selected) {
                    const filteredSubCategories = serviceCategories.filter(
                      (subCat) => {
                        return subCat.category === selected.value;
                      },
                    );
                    setServiceCategories(filteredSubCategories);
                  } else {
                    setServiceCategories([]);
                  }
                }}
                styles={selectStyles}
                formatOptionLabel={formatCategoryOption}
                placeholder={t("AddEventService.select_main_category")}
                isSearchable={true}
                isDisabled={categoriesLoading}
                isLoading={categoriesLoading}
                menuPlacement="auto"
              />
              {errors.mainCategory && (
                <div className={styles.errorText}>{errors.mainCategory}</div>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.service_sub_category")}
                <span className={styles.required}>*</span>
              </label>
              <Select
                options={
                  mainCategory
                    ? [
                        {
                          label: mainCategory.label,
                          options:
                            groupedServiceCategories[mainCategory.value]?.map(
                              (service) => ({
                                value: service.value,
                                label: service.label,
                                icon: service.icon,
                                category: mainCategory.value,
                              }),
                            ) || [],
                        },
                      ]
                    : []
                }
                value={serviceCategories}
                onChange={setServiceCategories}
                styles={selectStyles}
                formatOptionLabel={formatCategoryOption}
                placeholder={
                  !mainCategory
                    ? t("AddEventService.select_main_category_first")
                    : t("AddEventService.select_sub_category")
                }
                isSearchable={true}
                isMulti={true}
                isDisabled={!mainCategory || categoriesLoading}
                isLoading={categoriesLoading}
                menuPlacement="auto"
              />
              {errors.serviceCategory && (
                <div className={styles.errorText}>{errors.serviceCategory}</div>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.service_description")}
              </label>
              <div
                onClick={openDescriptionModal}
                style={{
                  minHeight: "120px",
                  padding: "12px 15px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  background: "#f8fafc",
                  cursor: "pointer",
                  fontSize: "15px",
                  color: form.serviceDescription ? "#1e293b" : "#94a3b8",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: "1.6",
                }}
              >
                {form.serviceDescription || t("AddEventService.describe_service") || "Click to add description..."}
              </div>
              {mainCategory && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginTop: "8px",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>💡 {t("AddEventService.description_tips")}:</strong>
                  <ul style={{ marginTop: "6px", paddingLeft: "20px" }}>
                    {getDescriptionSuggestions(mainCategory.value).map(
                      (tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.years_experience")}
              </label>
              <input
                type="number"
                name="experience"
                value={form.experience}
                onChange={handleChange}
                className={styles.input}
                min={0}
                placeholder="E.g., 5"
              />
            </div>

            <div className={styles.sectionTitle}>
              {t("AddEventService.location_details")}
            </div>

            <div className={styles.inputGroup}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label className={styles.label}>
                  {t("AddEventService.address")}
                  <span className={styles.required}>*</span>
                </label>
                <button 
                  type="button"
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    background: gettingLocation ? '#cbd5e1' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: gettingLocation ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {gettingLocation ? (
                    <>
                      <ButtonSpinner size={14} color="#fff" type="dots" />
                      <span>{t("AddEventService.getting_location", "Getting...")}</span>
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="3"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                      </svg>
                      <span>{t("AddEventService.use_current_location", "Use Current Location")}</span>
                    </>
                  )}
                </button>
              </div>
              <input 
                name="address" 
                value={form.address} 
                onChange={handleChange} 
                ref={addressInputRef}
                className={`${styles.input} ${errors.address ? styles.inputError : ''}`}
              />
              {errors.address && <div className={styles.errorText}>{errors.address}</div>}
              <div style={{ 
                fontSize: '13px', 
                color: '#667eea', 
                marginTop: '6px', 
                fontWeight: '600',
                background: '#f0f4ff',
                padding: '8px 12px',
                borderRadius: '6px',
                borderLeft: '3px solid #667eea'
              }}>
                💡 {t("AddEventService.address_hint", "Select address from dropdown to auto-fill City, State & Pincode")}
              </div>
            </div>
            <InputField
              label={t("AddEventService.pincode")}
              name="pincode"
              value={form.pincode}
              onChange={handleChange}
              error={errors.pincode}
              required
            />
            <InputField
              label={t("AddEventService.city")}
              name="city"
              value={form.city}
              onChange={handleChange}
              error={errors.city}
              required
            />
            <InputField
              label={t("AddEventService.state")}
              name="state"
              value={form.state}
              onChange={handleChange}
              error={errors.state}
              required
            />

            <div className={styles.sectionTitle}>
              {t("AddEventService.service_images")}
              {form.serviceImageUrls && (
                <span style={{ fontWeight: 500, color: '#64748b', fontSize: '15px' }}>
                  {" "}({form.serviceImageUrls.split(",").length})
                </span>
              )}
            </div>

            <div className={styles.imageSection}>
              {form.serviceImageUrls ? (
                <>
                  <div className={styles.imageGallery}>
                    {form.serviceImageUrls.split(",").map((url, idx) => (
                      <img
                        key={idx}
                        src={url.trim()}
                        alt={`Service ${idx + 1}`}
                        className={styles.imageItem}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className={styles.editImageButton}
                    onClick={() => setShowImageModal(true)}
                  >
                    <svg
                      width="24"
                      height="24"
                      fill="#667eea"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                    </svg>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={styles.addImageButton}
                  onClick={() => setShowImageModal(true)}
                >
                  <svg width="36" height="36" fill="#fff" viewBox="0 0 24 24">
                    <path
                      d="M12 5v14m-7-7h14"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </button>
              )}
            </div>
            {errors.serviceImageUrls && (
              <div className={styles.errorText}>{errors.serviceImageUrls}</div>
            )}

            <div className={styles.sectionTitle}>
              {t("AddEventService.pricing_details")}
            </div>

            <InputField
              label={t("AddEventService.minimum_price")}
              name="minPrice"
              value={form.minPrice}
              onChange={handleChange}
              error={errors.minPrice}
              type="number"
              min={0}
              required
            />
            <InputField
              label={t("AddEventService.maximum_price")}
              name="maxPrice"
              value={form.maxPrice}
              onChange={handleChange}
              type="number"
              min={0}
              placeholder={t("AddEventService.optional")}
            />

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.advance_booking_days")}
              </label>
              <input
                type="number"
                name="advanceBookingDays"
                value={form.advanceBookingDays}
                onChange={handleChange}
                className={styles.input}
                min={1}
                placeholder="E.g., 7 days in advance"
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.available_weekends")}
              </label>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="availableOnWeekends"
                    checked={form.availableOnWeekends === true}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        availableOnWeekends: true,
                      }))
                    }
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Yes</span>
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="availableOnWeekends"
                    checked={form.availableOnWeekends === false}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        availableOnWeekends: false,
                      }))
                    }
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>No</span>
                </label>
              </div>
            </div>

            <div className={styles.sectionTitle}>
              {t("AddEventService.additional_details")}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("AddEventService.id_proof_type")}
              </label>
              <Select
                options={ID_PROOF_TYPE_OPTIONS}
                value={idProofType}
                onChange={setIdProofType}
                styles={selectStyles}
                placeholder={t("AddEventService.select_id_proof_type")}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>

            <InputField
              label={t("AddEventService.id_proof_number")}
              name="ownerIdProofNumber"
              value={form.ownerIdProofNumber}
              onChange={handleChange}
              placeholder={t("AddEventService.optional")}
            />

            <DocumentUpload
              label={t("AddEventService.upload_id_proof")}
              imageUrl={form.ownerIdProofUrl}
              onUpload={() => setShowIdProofModal(true)}
            />

            {/* Business Documents Section - Hidden */}
            {/* 
            <div className={styles.sectionTitle}>
              {t("AddEventService.business_documents")}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "20px",
                padding: "12px",
                background: "#f1f5f9",
                borderRadius: "8px",
              }}
            >
              💡 {t("AddEventService.documents_optional_recommended")}
            </div>

            <InputField
              label={t("AddEventService.gst_number")}
              name="gstNumber"
              value={form.gstNumber}
              onChange={handleChange}
              error={errors.gstNumber}
              placeholder={t("AddEventService.gst_format")}
              maxLength="15"
            />
            <div
              style={{
                fontSize: "13px",
                color: "#64748b",
                marginTop: "-12px",
                marginBottom: "16px",
              }}
            >
              ℹ️ GST registration recommended for businesses with turnover above
              ₹20 lakhs
            </div>

            <InputField
              label={t("AddEventService.business_registration_number")}
              name="businessRegistrationNumber"
              value={form.businessRegistrationNumber}
              onChange={handleChange}
              placeholder={t("AddEventService.optional")}
            />

            <DocumentUpload
              label={t("AddEventService.upload_business_license")}
              imageUrl={form.businessLicenseUrl}
              onUpload={() => setShowBusinessLicenseModal(true)}
            />

            {serviceCategories.some((cat) =>
              ["full_catering", "veg_catering", "non_veg_catering"].includes(
                cat.value,
              ),
            ) && (
              <>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#f59e0b",
                    marginBottom: "12px",
                    padding: "10px",
                    background: "#fffbeb",
                    borderRadius: "6px",
                    border: "1px solid #fcd34d",
                  }}
                >
                  ⚠️ {t("AddEventService.fssai_recommended_caterers")}
                </div>
                <InputField
                  label={t("AddEventService.fssai_license_number")}
                  name="fssaiLicenseNumber"
                  value={form.fssaiLicenseNumber}
                  onChange={handleChange}
                  placeholder={t("AddEventService.optional")}
                />
                <DocumentUpload
                  label={t("AddEventService.upload_fssai_license")}
                  imageUrl={form.fssaiLicenseUrl}
                  onUpload={() => setShowFssaiModal(true)}
                />
              </>
            )}

            <DocumentUpload
              label={t("AddEventService.upload_insurance")}
              imageUrl={form.insuranceCertificateUrl}
              onUpload={() => setShowInsuranceModal(true)}
            />
            */}

            <div className={styles.termsRow}>
              <input
                type="checkbox"
                id="agree-terms-listing"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className={styles.termsCheckbox}
              />
              <label htmlFor="agree-terms-listing" className={styles.termsLabel}>
                I have read and agree to the{" "}
                <Link to="/terms-and-conditions" className={styles.termsLink}>
                  Terms &amp; Conditions
                </Link>
                {" "}and{" "}
                <Link to="/privacy-policy" className={styles.termsLink}>
                  Privacy Policy
                </Link>
              </label>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="submit"
                disabled={loading || !agreedToTerms}
                className={styles.submitButton}
              >
                {loading ? (
                  <span className={styles.loadingSpinner}>
                    <ButtonSpinner size={18} color="#fff" type="dots" />
                    <span>{t("AddEventService.adding_service")}</span>
                  </span>
                ) : (
                  t("AddEventService.add_service_button")
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className={styles.resetButton}
              >
                {t("AddEventService.reset")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ServiceImageModal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        initialImage={form.serviceImageUrls}
        maxImages={100}
        onUpload={async (files, keptUrls) => {
          const uploadedUrls = [];
          for (const file of files) {
            const base64 = await getOptimizedBase64ForUpload(file);
            const response = await uploadImageApi(userId, base64);
            if (
              response &&
              response.responseCode === 200 &&
              typeof response.responseData === "string"
            ) {
              uploadedUrls.push(response.responseData);
            }
          }
          const allUrls = [...keptUrls, ...uploadedUrls];
          setForm((prev) => ({ ...prev, serviceImageUrls: allUrls.join(",") }));
        }}
      />

      <DocumentUploadModal
        open={showIdProofModal}
        onClose={() => setShowIdProofModal(false)}
        initialImage={form.ownerIdProofUrl}
        userId={userId}
        onUpload={(url) => {
          setForm((prev) => ({ ...prev, ownerIdProofUrl: url }));
        }}
        title={t("AddEventService.upload_id_proof")}
      />

      <DocumentUploadModal
        open={showBusinessLicenseModal}
        onClose={() => setShowBusinessLicenseModal(false)}
        initialImage={form.businessLicenseUrl}
        userId={userId}
        onUpload={(url) => {
          setForm((prev) => ({ ...prev, businessLicenseUrl: url }));
        }}
        title={t("AddEventService.upload_business_license")}
      />

      <DocumentUploadModal
        open={showFssaiModal}
        onClose={() => setShowFssaiModal(false)}
        initialImage={form.fssaiLicenseUrl}
        userId={userId}
        onUpload={(url) => {
          setForm((prev) => ({ ...prev, fssaiLicenseUrl: url }));
        }}
        title={t("AddEventService.upload_fssai_license")}
      />

      <DocumentUploadModal
        open={showInsuranceModal}
        onClose={() => setShowInsuranceModal(false)}
        initialImage={form.insuranceCertificateUrl}
        userId={userId}
        onUpload={(url) => {
          setForm((prev) => ({ ...prev, insuranceCertificateUrl: url }));
        }}
        title={t("AddEventService.upload_insurance")}
      />

      {/* Description Editor Modal */}
      {showDescriptionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "12px",
          }}
          onClick={closeDescriptionModal}
        >
          <div
            style={{
              background: "white",
              borderRadius: "0",
              width: "100%",
              maxWidth: "620px",
              maxHeight: "82vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.24)",
              overflowX: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: "14px 16px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "600", color: "#1f2937" }}>
                ✍️ Edit Service Description
              </h3>
              <button
                onClick={closeDescriptionModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "28px",
                  color: "#9ca3af",
                  cursor: "pointer",
                  padding: "0",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "0",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#1f2937";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                  e.currentTarget.style.color = "#9ca3af";
                }}
              >
                ×
              </button>
            </div>

            {/* Formatting Toolbar */}
            <div
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                overflowX: "hidden",
              }}
            >
              <button
                type="button"
                onClick={() => handleGenerateAIDescription("temp")}
                disabled={aiGenerating}
                style={{
                  padding: "7px 10px",
                  background: aiGenerating ? "#cbd5e1" : "#2563eb",
                  border: "1px solid #2563eb",
                  borderRadius: "0",
                  cursor: aiGenerating ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  color: "white",
                  fontWeight: "600",
                }}
              >
                {aiGenerating
                  ? (aiLanguage === "hi" ? "हिंदी बन रहा है..." : aiLanguage === "mix" ? "Mix बन रहा है..." : "Generating...")
                  : (aiLanguage === "hi" ? "AI हिंदी" : aiLanguage === "mix" ? "AI Mix" : "AI Write")}
              </button>
              {[
                { key: "en", label: "EN" },
                { key: "hi", label: "हिंदी" },
                { key: "mix", label: "Mix" },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setAiLanguage(option.key)}
                  style={{
                    padding: "7px 10px",
                    background: aiLanguage === option.key ? "#0f172a" : "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    borderRadius: "0",
                    cursor: "pointer",
                    fontSize: "12px",
                    color: aiLanguage === option.key ? "white" : "#374151",
                    fontWeight: "600",
                  }}
                >
                  {option.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => insertFormatting('bold')}
                style={{
                  padding: "7px 10px",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#374151",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#3b82f6";
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.borderColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#374151";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('italic')}
                style={{
                  padding: "7px 10px",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0",
                  cursor: "pointer",
                  fontStyle: "italic",
                  fontSize: "13px",
                  color: "#374151",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#3b82f6";
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.borderColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#374151";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                I
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('bullet')}
                style={{
                  padding: "7px 10px",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#374151",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#3b82f6";
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.borderColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#374151";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                • List
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('number')}
                style={{
                  padding: "7px 10px",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#374151",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#3b82f6";
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.borderColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#374151";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                1. List
              </button>
            </div>

            {/* Textarea */}
            <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "16px", boxSizing: "border-box" }}>
              <textarea
                ref={descriptionTextareaRef}
                value={tempDescription}
                onChange={(e) => setTempDescription(e.target.value)}
                placeholder="Describe your service in detail... Use the formatting buttons above to style your text."
                style={{
                  width: "100%",
                  minHeight: "260px",
                  padding: "12px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "0",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  fontFamily: "inherit",
                  resize: "vertical",
                  outline: "none",
                  transition: "border-color 0.2s",
                  boxSizing: "border-box",
                  maxWidth: "100%",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#3b82f6";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              />
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "12px",
                  color: "#6b7280",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <span>
                  💡 Tip: Use **bold**, *italic*, • bullets, and 1. numbered lists
                </span>
                <span>
                  {tempDescription.length} characters
                </span>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "14px 16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={closeDescriptionModal}
                style={{
                  padding: "10px 18px",
                  background: "#f3f4f6",
                  border: "none",
                  borderRadius: "0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#e5e7eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveDescription}
                style={{
                  padding: "10px 18px",
                  background: "#3b82f6",
                  border: "none",
                  borderRadius: "0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "white",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#2563eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#3b82f6";
                }}
              >
                Save & Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100%",
          zIndex: 100,
        }}
      >
        <BottomNav />
      </div>

      <StatusModal
        open={modal.open}
        message={modal.message}
        success={modal.success}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
      />

      {showToast && (
        <Toast
          message={toastMessage}
          success={false}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}

function InputField({ label, inputRef, error, required, ...props }) {
  return (
    <div className={styles.inputGroup}>
      <label className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      <input
        {...props}
        ref={inputRef}
        className={`${styles.input} ${error ? styles.inputError : ""}`}
      />
      {error && <div className={styles.errorText}>{error}</div>}
    </div>
  );
}

function DocumentUpload({ label, imageUrl, onUpload }) {
  return (
    <div className={styles.documentUpload}>
      <label className={styles.documentLabel}>{label}</label>
      <div className={styles.documentPlaceholder}>
        {imageUrl ? "Document uploaded" : "No document uploaded"}
      </div>
      <button
        type="button"
        onClick={onUpload}
        className={styles.documentUploadButton}
        title="Upload Document"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </button>
      {imageUrl && (
        <div className={styles.documentPreview}>
          <img src={imageUrl} alt="ID Proof" />
        </div>
      )}
    </div>
  );
}
